import React, { forwardRef, useMemo, useState, useEffect, useCallback } from "react";
import { Button, Drawer, Empty, Input, Select as AntdSelect } from "antd";
import { CheckOutlined, CloseCircleFilled, DownOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getNodeText, isSameValue, toArray } from "../ResponsivePicker/utils";
import "../ResponsivePicker/style.css";

const { Option, OptGroup } = AntdSelect;

const optionChildrenToOptions = (children) => {
  const result = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === OptGroup || child.props?.options) {
      result.push(...optionChildrenToOptions(child.props.children));
      return;
    }
    result.push({
      value: child.props.value ?? child.key,
      label: child.props.children,
      displayLabel: child.props.displayLabel ?? child.props.title ?? child.props.label ?? child.props.children,
      searchText: child.props.searchText ?? child.props.label,
      disabled: child.props.disabled,
    });
  });
  return result;
};

const normalizeOptions = (options, children) => {
  const source = options || optionChildrenToOptions(children);
  return (source || []).flatMap((item) => {
    if (item.options) return normalizeOptions(item.options);
    return [{
      value: item.value,
      label: item.label ?? item.children ?? item.value,
      displayLabel: item.displayLabel ?? item.mobileLabel ?? item.label ?? item.children ?? item.value,
      searchText: item.searchText ?? item.filterText,
      disabled: item.disabled,
      raw: item,
    }];
  });
};

const renderQuickActions = (quickActions, onClose) => {
  const actions = (quickActions || []).filter(Boolean);
  if (!actions.length) return null;

  return (
    <div className="responsive-picker-quick-actions">
      {actions.map((action) => (
        <button
          key={action.key || action.label}
          type="button"
          className="responsive-picker-quick-action"
          disabled={action.disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            if (action.disabled) return;
            action.onClick?.();
            onClose?.();
          }}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
};

const hasSelectAllAction = (quickActions) => {
  return (quickActions || []).some((action) => {
    const key = String(action?.key || "").toLowerCase();
    const label = String(action?.label || "").toLowerCase().replace(/\s+/g, "");
    return key.includes("select-all") || label === "selectall";
  });
};

const ResponsiveSelect = forwardRef((props, ref) => {
  const {
    options,
    children,
    value,
    defaultValue,
    onChange,
    onFilterValueChange,
    name,
    placeholder = "Please select",
    disabled,
    allowClear,
    mode,
    showSearch,
    optionFilterProp = "label",
    className,
    style,
    popupTitle,
    quickActions,
    forceMobileDrawer = false,
    ...rest
  } = props;
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const useDesktopSelect = isPC && !forceMobileDrawer;
  const isMultiple = mode === "multiple" || mode === "tags";
  const normalizedOptions = useMemo(() => normalizeOptions(options, children), [options, children]);
  const controlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(defaultValue);
  const currentValue = controlled ? value : innerValue;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [draftValue, setDraftValue] = useState(currentValue);
  const selectableOptions = useMemo(
    () => normalizedOptions.filter((option) => !option.disabled),
    [normalizedOptions]
  );
  const selectAllValue = useMemo(
    () => selectableOptions.map((option) => option.value),
    [selectableOptions]
  );

  const notifyFilterValueChange = useCallback((nextValue) => {
    onFilterValueChange?.({
      [name || placeholder || "select"]: nextValue,
    });
  }, [name, onFilterValueChange, placeholder]);

  const emitChange = useCallback((nextValue, option) => {
    if (!controlled) setInnerValue(nextValue);
    notifyFilterValueChange(nextValue);
    onChange?.(nextValue, option);
  }, [controlled, notifyFilterValueChange, onChange]);

  const handleSelectAll = useCallback(() => {
    if (!isMultiple) return;
    setDraftValue(selectAllValue);
    emitChange(selectAllValue, selectableOptions.map((option) => option.raw));
  }, [emitChange, isMultiple, selectAllValue, selectableOptions]);

  const mergedQuickActions = useMemo(() => {
    if (!isMultiple || hasSelectAllAction(quickActions)) {
      return quickActions;
    }

    return [
      ...(quickActions || []),
      {
        key: "select-all",
        label: "Select All",
        disabled: disabled || selectableOptions.length === 0,
        onClick: handleSelectAll,
      },
    ];
  }, [disabled, handleSelectAll, isMultiple, quickActions, selectableOptions.length]);

  useEffect(() => {
    if (open) setDraftValue(currentValue);
  }, [currentValue, open]);

  if (useDesktopSelect) {
    const originalDropdownRender = rest.dropdownRender;
    const hasQuickActions = (mergedQuickActions || []).filter(Boolean).length > 0;
    const dropdownRender = (menu) => {
      const renderedMenu = originalDropdownRender ? originalDropdownRender(menu) : menu;
      const quickActionContent = renderQuickActions(mergedQuickActions);

      if (!quickActionContent) return renderedMenu;

      return (
        <>
          {renderedMenu}
          {quickActionContent}
        </>
      );
    };

    return (
      <AntdSelect
        ref={ref}
        {...rest}
        dropdownRender={hasQuickActions || originalDropdownRender ? dropdownRender : undefined}
        options={options}
        value={value}
        defaultValue={defaultValue}
        onChange={(nextValue, option) => {
          notifyFilterValueChange(nextValue);
          onChange?.(nextValue, option);
        }}
        onSelect={rest.onSelect}
        onClear={rest.onClear}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={allowClear}
        mode={mode}
        showSearch={showSearch}
        optionFilterProp={optionFilterProp}
        className={className}
        style={style}
      >
        {children}
      </AntdSelect>
    );
  }

  const selectedValues = toArray(currentValue);
  const draftValues = toArray(draftValue);
  const selectedLabels = normalizedOptions
    .filter((option) => selectedValues.some((item) => isSameValue(item, option.value)))
    .map((option) => getNodeText(option.displayLabel));
  const displayValue = selectedLabels.join(", ");
  const filteredOptions = search
    ? normalizedOptions.filter((option) => {
        const target =
          optionFilterProp === "value"
            ? option.value
            : option.raw?.[optionFilterProp] ?? option.searchText ?? option.label;
        return getNodeText(target).toLowerCase().includes(search.toLowerCase());
      })
    : normalizedOptions;

  const toggleDraftValue = (option) => {
    if (option.disabled) return;
    if (!isMultiple) {
      emitChange(option.value, option.raw);
      setOpen(false);
      return;
    }
    const exists = draftValues.some((item) => isSameValue(item, option.value));
    const next = exists
      ? draftValues.filter((item) => !isSameValue(item, option.value))
      : [...draftValues, option.value];
    setDraftValue(next);
  };

  const clearValue = (event) => {
    event.stopPropagation();
    emitChange(isMultiple ? [] : undefined, undefined);
  };

  const openPicker = () => {
    if (disabled) return;
    rest.onFocus?.();
    setOpen(true);
  };

  return (
    <>
      <div
        ref={ref}
        className={`responsive-picker-trigger${disabled ? " responsive-picker-trigger-disabled" : ""} ${className || ""}`}
        style={style}
        onClick={openPicker}
      >
        <span className={`responsive-picker-trigger-value${displayValue ? "" : " responsive-picker-trigger-placeholder"}`}>
          {displayValue || placeholder}
        </span>
        <span className="responsive-picker-trigger-icons">
          {allowClear && displayValue && !disabled ? <CloseCircleFilled className="responsive-picker-clear" onClick={clearValue} /> : null}
          <DownOutlined />
        </span>
      </div>
      <Drawer
        open={open}
        title={popupTitle || placeholder}
        placement="bottom"
        height="72vh"
        zIndex={1120}
        className="responsive-picker-drawer"
        onClose={() => setOpen(false)}
        destroyOnClose
      >
        <div className="responsive-picker-body">
          {showSearch && (
            <Input
              className="responsive-picker-search"
              allowClear
              value={search}
              onChange={(event) => {
                const nextSearch = event.target.value;
                setSearch(nextSearch);
                rest.onSearch?.(nextSearch);
              }}
              placeholder="Search"
            />
          )}
          {filteredOptions.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            filteredOptions.map((option) => {
              const checked = (isMultiple ? draftValues : selectedValues).some((item) => isSameValue(item, option.value));
              return (
                <div
                  key={String(option.value)}
                  className={`responsive-picker-option${option.disabled ? " responsive-picker-option-disabled" : ""}`}
                  onClick={() => toggleDraftValue(option)}
                >
                  <span className="responsive-picker-option-label">{option.label}</span>
                  {checked ? <CheckOutlined className="responsive-picker-option-check" /> : null}
                </div>
              );
            })
          )}
          {renderQuickActions(mergedQuickActions, () => setOpen(false))}
        </div>
        {isMultiple && (
          <div className="responsive-picker-footer">
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              onClick={() => {
                emitChange(draftValues, normalizedOptions.filter((option) => draftValues.some((item) => isSameValue(item, option.value))));
                setOpen(false);
              }}
            >
              Confirm
            </Button>
          </div>
        )}
      </Drawer>
    </>
  );
});

ResponsiveSelect.displayName = "ResponsiveSelect";

ResponsiveSelect.Option = Option;
ResponsiveSelect.OptGroup = OptGroup;
ResponsiveSelect.SECRET_COMBOBOX_MODE_DO_NOT_USE = AntdSelect.SECRET_COMBOBOX_MODE_DO_NOT_USE;

export default ResponsiveSelect;
